/**
 * Visszaigazolásra vár — `build/FIXES.md` item 79 (F8), Barna's ruling 9 of
 * 8 October 2026.
 *
 * The queue for *confirm first, pay after*: fully priced orders that need a
 * person before they can be priced for certain, with **nothing paid**. It
 * sits beside Jóváhagyásra vár rather than replacing it — the review queue is
 * §11.4's pay-first lane and that lane is exactly what it was.
 *
 * Two actions here, and the third is elsewhere on purpose: approve and
 * decline are this screen's; *approve with an amendment* is the rendelés­-
 * szerkesztő's (§11.11), because building the amended version is what the
 * editor is for, and the order comes back here to be approved once it is
 * applied.
 *
 * The customer's comment is shown here and in the staff notification and
 * nowhere else: item 79's data-handling rule. It is free text and may contain
 * dietary detail, so it is not on a kitchen view, a label or an invoice.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import {
  asDate,
  asMoment,
  fmt,
  Loading,
  REVIEW_REASON_HU,
  Tags,
  useConsoleAction,
  useConsoleData,
} from '../common.tsx'
import { Against } from './Day.tsx'

/** The thirteen of `public.confirmation_reason`, as a colleague reads them.
 *  The same Hungarian the customer saw on the page, shortened to the noun a
 *  queue is skimmed by — the pattern `REVIEW_REASON_HU` already sets.
 *
 *  `build/FIXES.md` item 81 (P2) adds the last six. They are the **only** six
 *  the customer never saw, so their labels are not taken from the page: each
 *  is §11.4's own word for the rule, copied from `REVIEW_REASON_HU` so that a
 *  colleague reads the same thing in the review queue and in this one. Item
 *  81: "Staff see all reasons in C2 and the console." */
export const CONFIRMATION_REASON_HU: Record<string, string> = {
  staff_equipment: 'személyzet és eszközök',
  coffee: 'kávé, személyzettel',
  dietary_note: 'allergia vagy étrendi igény',
  custom_menu: 'egyedi menü',
  other_food: 'más étel is lesz',
  contract_value: 'értékhatár fölött',
  customer_comment: 'megjegyzés',
  value: 'értékhatár felett',
  covers: 'vendégszám felett',
  new_customer_value: 'új vendég, értékhatár felett',
  clash_date: 'ütközéses nap',
  zone_c: 'C zóna',
  capacity_soft: 'kapacitás puha küszöb',
  /* Run 9, item 4: over portion_cap or the day's guest limit. Sent for
     confirmation instead of refused. `other_food` above stays, for the
     orders that carry it (run 9 item 2 stopped writing it). */
  capacity_over: 'napi kapacitás felett',
  /* Run 13a item 2 (DECIDED 20261012): a transfer recorded after the order
     expired. Approval confirms it and issues the előlegszámla; a decline
     refunds it. */
  late_payment: 'késve érkezett fizetés',
}

/** Run 13b (DECIDED 20261012): why a late_payment order cannot be approved.
 *  The codes are the ones a new order is refused with (§11.3). */
export const LATE_REFUSAL_HU: Record<string, string> = {
  closed_day: 'a rendezvény napja zárva van',
  too_late: 'lejárt a rendelési határidő',
  capacity_full: 'a napi rendelésszám e rendelés nélkül is betelt',
}

/** `public.order_late_approval_check`: the date checks of a new order. */
interface LateCheck {
  refusals: string[]
  capacity_over: boolean
  capacity_soft: boolean
  lane_window: string
  /** The day including this order. */
  load: {
    orders: number
    max_orders_per_day: number | null
    portions: number
    portion_cap: number | null
    covers: number
    max_covers_per_day: number | null
  }
}

interface QueueOrder {
  id: string
  ref: string
  event_name: string | null
  event_date: string
  guests: number
  gf_guests: number
  customer_name: string
  customer_company: string | null
  gross: number
  confirmation_reasons: string[]
  review_reasons: string[]
  customer_comment: string | null
  dietary_note: string | null
  entered_at: string | null
  waiting_working_h: number
  answer_due: string | null
  needs_dietary_signoff: boolean
  dietary_signoff: { decision: string; resolution: string } | null
  /** Run 13b: present on a late_payment order only. */
  late_check?: LateCheck | null
}

interface Queue {
  orders: QueueOrder[]
  reminder_working_h: number | null
  escalation_working_h: number | null
  /** Run 12 item 5: the link's window, payment_window_bd business days. */
  payment_window_bd: number | null
}

export function Confirmations(): JSX.Element {
  const { data, error, loading, reload } = useConsoleData<Queue>('confirmation_queue', {})
  const { run, busy, error: actionError, moved } = useConsoleAction()
  const [open, setOpen] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  async function act(action: string, args: Record<string, unknown>): Promise<void> {
    const ok = await run(action, args)
    if (ok) {
      setOpen(null)
      setReason('')
    }
    if (ok || moved.current) reload()
  }

  const escalation = data?.escalation_working_h ?? null
  const reminder = data?.reminder_working_h ?? null
  const window = data?.payment_window_bd ?? null

  return (
    <section data-testid="view-confirmations">
      <h2>Visszaigazolásra vár</h2>
      <p className="lead">
        Ezekért a rendelésekért még nem fizettek: a vendég a visszaigazolás után kap fizetési
        linket.
        {window !== null ? ` A linknek ${fmt.n(window)} munkanapig, de legkésőbb a véglegesítésig van érvénye.` : ''}
        {reminder !== null && escalation !== null
          ? ` Emlékeztető ${fmt.n(reminder)}, eszkaláció ${fmt.n(escalation)} munkaóra után.`
          : ''}
      </p>

      <Loading loading={loading} error={error} />
      {actionError !== null && (
        <p className="note error" data-testid="action-error">{actionError}</p>
      )}

      {data !== null && data.orders.length === 0 && (
        <p className="note" data-testid="confirmations-empty">
          Nincs visszaigazolásra váró rendelés.
        </p>
      )}

      {data !== null && data.orders.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Rendelés</th>
              <th>Rendezvény</th>
              <th className="num">Vendég</th>
              <th>Ok</th>
              <th className="num">Várakozás</th>
              <th>Művelet</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((order) => {
              const latePayment = order.confirmation_reasons.includes('late_payment')
              const check = order.late_check ?? null
              const refused = check !== null && check.refusals.length > 0
              const late = escalation !== null && order.waiting_working_h >= escalation
              const due = !late && reminder !== null && order.waiting_working_h >= reminder
              return (
                <tr key={order.id} data-testid={`confirmation-${order.ref}`}>
                  <td>
                    <Link to={`/staff/orders/${order.id}`}>{order.ref}</Link>
                    <div className="tag">{order.customer_company ?? order.customer_name}</div>
                    <div className="tag">{fmt.money(order.gross)}</div>
                  </td>
                  <td>
                    {asDate(order.event_date, true)}
                    {order.event_name !== null && <div className="tag">{order.event_name}</div>}
                  </td>
                  <td className="num">
                    {fmt.n(order.guests)}
                    {order.gf_guests > 0 ? ` (${fmt.n(order.gf_guests)} GM)` : ''}
                  </td>
                  <td>
                    <Tags
                      values={order.confirmation_reasons.map(
                        (code) => CONFIRMATION_REASON_HU[code] ?? code,
                      )}
                    />
                    {order.review_reasons.length > 0 && (
                      <Tags values={order.review_reasons.map((r) => REVIEW_REASON_HU[r] ?? r)} />
                    )}
                    {/* Item 79, data handling: the comment is shown here and
                        in the staff notification, and on no kitchen view,
                        label or invoice. */}
                    {order.customer_comment !== null && (
                      <div className="note" data-testid={`comment-${order.ref}`}>
                        {order.customer_comment}
                      </div>
                    )}
                    {latePayment && (
                      <div className="tag warn" data-testid={`late-payment-${order.ref}`}>
                        a pénz megérkezett: jóváhagyáskor visszaigazolt, előlegszámla a jóváírás napjával
                      </div>
                    )}
                    {/* Run 13b: the date checks of a new order, before
                        anyone presses approve. */}
                    {check !== null && (
                      <div data-testid={`late-check-${order.ref}`}>
                        {refused && (
                          <div className="tag stop" data-testid={`late-refused-${order.ref}`}>
                            nem hagyható jóvá:{' '}
                            {check.refusals.map((code) => LATE_REFUSAL_HU[code] ?? code).join(', ')}.
                            Csak elutasítható; az átutalás visszatérítendő lesz.
                          </div>
                        )}
                        {!refused && check.capacity_over && (
                          <div className="tag warn" data-testid={`late-over-${order.ref}`}>
                            napi kapacitás felett, de jóváhagyható
                          </div>
                        )}
                        <div className="note" data-testid={`late-load-${order.ref}`}>
                          A nap terhelése ezzel a rendeléssel: rendelés{' '}
                          <Against used={check.load.orders} limit={check.load.max_orders_per_day} />{' '}
                          vendég{' '}
                          <Against used={check.load.covers} limit={check.load.max_covers_per_day} />{' '}
                          adag{' '}
                          <Against used={check.load.portions} limit={check.load.portion_cap} />
                        </div>
                      </div>
                    )}
                    {order.needs_dietary_signoff && (
                      <div className="tag stop" data-testid={`needs-signoff-${order.ref}`}>
                        aláírás nélkül nem hagyható jóvá
                      </div>
                    )}
                  </td>
                  <td className="num">
                    <span className={late ? 'tag stop' : due ? 'tag warn' : 'tag'}>
                      {fmt.n(order.waiting_working_h, 1)} munkaóra
                    </span>
                    <div className="tag">{asMoment(order.entered_at)}</div>
                    {order.answer_due !== null && (
                      <div className="tag">válasz: {asMoment(order.answer_due)}</div>
                    )}
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        type="button"
                        className="primary"
                        disabled={busy || refused}
                        onClick={() =>
                          void act('confirmation_approve', { order_id: order.id })}
                        data-testid={`approve-${order.ref}`}
                      >
                        Visszaigazolás
                      </button>
                      {/* §11.11: the amended version is the editor's to
                          build. It comes back here to be approved. */}
                      <Link className="button" to={`/staff/editor?order=${order.id}`}>
                        Módosítás
                      </Link>
                      <button
                        type="button"
                        className="danger"
                        disabled={busy}
                        onClick={() => setOpen(order.id)}
                        data-testid={`decline-${order.ref}`}
                      >
                        Elutasítás
                      </button>
                    </div>

                    {open === order.id && (
                      <div className="card" data-testid={`form-decline-${order.ref}`}>
                        <h3>Elutasítás indoka</h3>
                        <label htmlFor={`reason-${order.id}`}>
                          {latePayment
                            ? 'Ez az indok megy ki a vendégnek (U6). Az átutalás már megérkezett: az összeg visszatérítendőként kerül a Visszatérítések nézetbe (F4).'
                            : 'Ez az indok megy ki a vendégnek (U6). Fizetés nem történt, így nincs mit visszatéríteni.'}
                        </label>
                        <textarea
                          id={`reason-${order.id}`}
                          rows={3}
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                          data-testid="reason"
                        />
                        <div className="actions">
                          <button
                            type="button"
                            className="primary"
                            disabled={busy || reason.trim() === ''}
                            onClick={() =>
                              void act('confirmation_decline', { order_id: order.id, reason })}
                            data-testid="reason-send"
                          >
                            Küldés
                          </button>
                          <button type="button" onClick={() => setOpen(null)}>Mégse</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}
